import { z } from "zod";

// AR byte-park contract (ADR-0035 §3, issue #379). The **AR bake** (CONTEXT.md)
// flattens the current scene client-side into two self-contained files — a GLB
// (Android Scene Viewer / WebXR) and a USDZ (iOS Quick Look) — and parks them at
// a short-lived `https` URL. The park is required because the OS AR engines run
// **out-of-process** and cannot fetch a `blob:` (they crash), so the bytes must
// live at a real, fetchable URL served with the correct MIME. NO conversion
// happens server-side (branch A, ADR-0035 §4): the endpoint stores the bytes the
// client already baked, one object per format.
export const AR_ASSET_FORMATS = ["glb", "usdz"] as const;
export type ArAssetFormat = (typeof AR_ASSET_FORMATS)[number];
export const ArAssetFormatSchema = z.enum(AR_ASSET_FORMATS);

// The Content-Type each format MUST be served with so the OS AR engine accepts
// it (ADR-0035 §3). Server-authoritative: the park endpoint stamps these onto
// the S3 object at PUT time, so the signed GET returns the correct MIME
// regardless of what the client sent — the client never gets to pick the MIME.
export const AR_ASSET_MIME: Record<ArAssetFormat, string> = {
  glb: "model/gltf-binary",
  usdz: "model/vnd.usdz+zip",
};

// File extension per format — the tail of the S3 key (and thus the fetch URL).
// The extension is part of the contract, not cosmetic: iOS Quick Look keys its
// USDZ handling off the `.usdz` suffix on the URL (ADR-0035 §3).
export const AR_ASSET_EXTENSION: Record<ArAssetFormat, string> = {
  glb: "glb",
  usdz: "usdz",
};

// Dedicated short-lived key prefix for parked AR assets — a sibling of
// `projects/` / `static/` in the same bucket (ADR-0007 single-bucket-with-
// prefixes). An S3 lifecycle rule on this prefix expires objects automatically
// (short TTL), so nothing parked here persists — an ephemeral render, not a
// stored artifact (ADR-0035 §3).
export const AR_ASSET_PREFIX = "ar/";

// Deterministic key for a parked AR asset given an unguessable id. The id (a
// random UUID minted server-side) is the whole security model for this ungated
// surface (ADR-0035 §7): no ownership check, so the key must not be enumerable.
// No owner / session / project id ever appears in the key, so a parked object
// carries no PII.
export const arAssetKey = (id: string, format: ArAssetFormat): string =>
  `${AR_ASSET_PREFIX}${id}.${AR_ASSET_EXTENSION[format]}`;

// A parked asset's public NAME — the S3 key with the `ar/` prefix stripped:
// `<uuidv4>.<glb|usdz>`. This is the short, stable handle the client addresses a
// parked object by (via `arAssetGatewayPath`) instead of carrying a ~1 KB signed
// URL. `crypto.randomUUID()` always mints a v4, so the shape is pinned to v4: the
// gateway rebuilds the S3 key as `ar/<name>` and signs it, so validating this shape
// FIRST guarantees the only signable key is `ar/<uuid>.<ext>` — an arbitrary or
// path-traversing name can never become a signing oracle over the bucket (§7).
const AR_ASSET_EXT_ALTERNATION = AR_ASSET_FORMATS.map((f) => AR_ASSET_EXTENSION[f]).join("|");
export const AR_ASSET_NAME_PATTERN = new RegExp(
  `^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\\.(${AR_ASSET_EXT_ALTERNATION})$`,
);

/** Whether `name` is a well-formed parked-asset name (`<uuidv4>.<glb|usdz>`). */
export const isArAssetName = (name: string): boolean => AR_ASSET_NAME_PATTERN.test(name);

// The public gateway that resolves a parked asset's `name` to a FRESH short-lived
// signed URL via a 302 (ADR-0035 §7). The desktop → phone QR handoff can only carry
// a SHORT URL — a signed S3 URL (an STS session token alone is ~1 KB) overflows a
// scannable QR — so the QR/launch URL addresses the bytes by this short handle and
// the API re-signs on each open. Same `/ar-assets/` prefix as the park PUT, so it
// shares the session-exempt, ungated capability plane (app.ts). One SSOT for both
// the API route registration and the client that builds the URL.
export const AR_ASSET_GATEWAY_PREFIX = "/ar-assets/parked/";
export const arAssetGatewayPath = (name: string): string => `${AR_ASSET_GATEWAY_PREFIX}${name}`;

// Wire response for `PUT /ar-assets/{format}`: the parked asset's short, stable
// public NAME (`<uuidv4>.<ext>`) — NOT a signed URL. The client builds the gateway
// URL from it (`arAssetGatewayPath`) and hands THAT to `<model-viewer>` / folds it
// into the desktop QR; the gateway 302s to a freshly-signed URL on each open.
// Returning the name (not a ~1 KB signed URL) is precisely what keeps the desktop QR
// scannable (ADR-0035 §7). Validated to the name shape, so a malformed value fails at
// the client boundary, never downstream.
export const ArAssetParkResponse = z.object({
  name: z.string().regex(AR_ASSET_NAME_PATTERN),
});
export type ArAssetParkResponse = z.infer<typeof ArAssetParkResponse>;
